import React, { ReactNode, useCallback, useMemo } from "react";

import { useForm, useWatch } from "antd/es/form/Form";
import Typography from "antd/es/typography";
import { useIntl } from "react-intl";

import User from "@mapmycustomers/shared/types/User";
import { Alert, Button, Modal, ModalProps } from "@mapmycustomers/ui";

import OrganizationSetting from "@app/enum/OrganizationSetting";
import ReportDeliveryType from "@app/enum/ReportDeliveryType";
import Iam from "@app/types/Iam";
import Report from "@app/types/Report";
import { formatDate } from "@app/util/formatters";

import EditScheduleForm, { FormState } from "./EditScheduleForm";
import styles from "./EditScheduleForm.module.scss";
import messages from "./messages";

export interface EditScheduleModalRef {
  show: () => void;
}

interface Props {
  afterClose: ModalProps["afterClose"];
  me: Iam;
  onClose: () => void;
  onSubmit: (
    report: Omit<Report, "frequency" | "nextRunAt" | "sendOptions"> & Partial<Report>
  ) => void;
  open: boolean;
  report?: Report;
  users: User[];
}

const EditScheduleModal = ({ afterClose, me, onClose, onSubmit, open, report, users }: Props) => {
  const intl = useIntl();

  const [form] = useForm<FormState>();
  const exportPreference = useWatch("sendFullReport", form);

  //fetching organization timezone
  const timezone = me.organization.settings.find(
    (keys) => keys.key === OrganizationSetting.TIMEZONE
  )?.value;

  const emails = useMemo(() => {
    const emails = users.map(({ username }) => username).concat(report?.sendOptions?.to ?? []);
    return Array.from(new Set(emails));
  }, [users, report]);

  const stopSendHandler = () => {
    if (!report) {
      return onClose();
    }
    submitHandler({ ...form.getFieldsValue(), frequency: "" });
  };
  // converting date time to format supported by backend
  const selectedDateTime = useCallback(
    (dateTime: Date | null) => dateTime && formatDate(new Date(dateTime), "yyyy-MM-dd HH:mm:ss"),
    []
  );

  const submitHandler = (formState?: FormState) => {
    if (!report) {
      return;
    }
    if (formState) {
      onSubmit({
        ...report,
        frequency: formState.frequency || undefined,
        // ensure that date is at least 10 minutes later than now
        nextRunAt: selectedDateTime(formState?.dateTime),
        sendOptions: {
          deliveryType: ReportDeliveryType.ATTACHMENT,
          sendFullReport: formState.sendFullReport,
          to: formState.recipients,
        },
        timezone: formState.timezone,
      });
    }
    onClose();
  };

  if (!report) {
    return null;
  }

  return (
    <Modal
      afterClose={afterClose}
      destroyOnClose
      footer={[
        report.frequency ? (
          <Button danger key="stopSend" onClick={stopSendHandler}>
            {intl.formatMessage(messages.stop)}
          </Button>
        ) : (
          <Button key="cancel" onClick={onClose}>
            {intl.formatMessage(messages.cancel)}
          </Button>
        ),
        <Button key="save" onClick={form.submit} type="primary">
          {report.frequency || report.nextRunAt
            ? intl.formatMessage(messages.saveChanges)
            : intl.formatMessage(messages.scheduleExports)}
        </Button>,
      ]}
      onCancel={onClose}
      open={open}
      title={
        <Typography.Title level={5}>
          {report.frequency || report.nextRunAt
            ? intl.formatMessage(messages.edit)
            : intl.formatMessage(messages.schedule)}
        </Typography.Title>
      }
      width={550}
    >
      <EditScheduleForm
        emails={emails}
        form={form}
        myEmail={me.username}
        onFinish={submitHandler}
        report={report}
        timezone={timezone}
      />
      <Alert
        className={styles.alert}
        message={
          <div className={styles.alertContainer}>
            <span>
              {intl.formatMessage(exportPreference ? messages.allRecords : messages.alert, {
                b: (text: ReactNode[]) => <strong>{text}</strong>,
              })}
            </span>
            <span>
              {intl.formatMessage(
                exportPreference ? messages.greatForTeams : messages.withExample,
                { b: (text: ReactNode[]) => <strong>{text}</strong> }
              )}
            </span>
          </div>
        }
        showIcon
        type="info"
      />
    </Modal>
  );
};

export default EditScheduleModal;
